import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  useCloneTemplateMutation,
  useCreateTemplateMutation,
  useDeleteTemplateMutation,
  useGetTemplateQuery,
  useGetTemplateReferencesQuery,
  useUpdateTemplateMutation,
} from '../../../store/templateLibrary/templateLibrary'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthenticatedUser } from '../../../store/auth/authSlice'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { createPortal } from 'react-dom'
import { TemplateEditorHeader } from './TemplateEditorHeader'
import { TemplateEditorBox, UnopenableTemplateBox } from './TemplateEditorBox'
import { EmailTemplateEditorBox } from './EmailTemplateEditor/EmailTemplateEditorBox'
import { hasLexicalEmailFormat } from './EmailTemplateEditor/emailTemplateFormat'
import { Col, Row, Spinner } from 'react-bootstrap'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { TemplateDetailsBox } from './TemplateDetailsBox'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { getTemplateLibraryTrackingDetails } from './tracking'
import { useAccount } from '../../../common/useAccount'
import { TemplateAIChat } from './TemplateAIChat'
import { type TemplateOrigin } from './templateOrigin'
import { TemplateDuplicateModal } from './TemplateDuplicateModal'
import { type TemplateCloneRequest, type TemplateType } from '../../../models/TemplateLibrary'
import { useCreateNodeMutation, useUpdateNodeMutation } from '../../../store/ai/inputParametersHierarchy'
import { calculateHierarchyId, isAdminManagedTemplate } from './utils'
import { SchedulingEmailViewBox } from './SchedulingEmails/SchedulingEmailViewBox'

export const TemplateEditor: React.FC = () => {
  const { tracked, trackingClient } = useTracking()
  const user = useAppSelector(selectAuthenticatedUser)
  const { portalElement } = useContext(HeaderContext)
  const { hasScope } = useZocksAuth()
  const hasTemplateAdminScope = hasScope('**', 'tmpl_lib:admin', ['tmpl_lib:admin'])
  const { accountId, account } = useAccount()
  const { templateId = '' } = useParams()
  const location = useLocation()
  const locationState = location.state as {
    aiAgentId?: string
    initialMessages?: Array<{ type: 'user' | 'agent'; message: string }>
    origin?: TemplateOrigin
  } | null
  const initialAiAgentId = locationState?.aiAgentId ?? null
  const initialMessages = locationState?.initialMessages
  const origin = locationState?.origin
  const [hasAIPendingChanges, setHasAIPendingChanges] = useState(false)
  const navigate = useNavigate()
  const { data: templateValue } = useGetTemplateQuery({ accountId, templateId })
  const { data: templateReferencesData } = useGetTemplateReferencesQuery({ accountId, templateId }, { skip: !templateId, refetchOnMountOrArgChange: true })
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateTemplateMutation()
  const [cloneTemplate, { isLoading: isCloning }] = useCloneTemplateMutation()
  const [createTemplate, { isLoading: isCreating }] = useCreateTemplateMutation()
  const [deleteTemplate] = useDeleteTemplateMutation()
  const [createNode] = useCreateNodeMutation()
  const [updateNode] = useUpdateNodeMutation()
  const canEditTemplate =
    !account?.enterpriseConfig.lockTemplates &&
    !!templateValue &&
    templateValue.scope !== 'enterprise' &&
    (templateValue.scope === 'user' || templateValue.scope === 'system' || (templateValue.scope === 'account' && hasTemplateAdminScope))

  const [localTemplate, setLocalTemplate] = useState<string | null>(null)
  const [aiIsLoading, setAiIsLoading] = useState(false)
  const [pendingSaveContent, setPendingSaveContent] = useState<TemplateType | null>(null)
  const bypassBlockerRef = useRef(false)
  const currentEditorContentRef = useRef<string | null>(null)
  const handleEditorChange = useCallback((html: string) => {
    currentEditorContentRef.current = html
  }, [])
  const _handleTemplateChange = async (template: TemplateType): Promise<void> => {
    if (!templateValue) {
      return
    }
    await updateTemplate({
      accountId,
      templateId: templateValue.id,
      request: { template },
      suppressGlobalErrorToast: true,
    }).unwrap()
    setHasAIPendingChanges(false)
  }

  const handleDuplicateFromSystem = async (request: TemplateCloneRequest) => {
    if (!templateValue || pendingSaveContent === null) {
      return
    }
    const isEmail = hasLexicalEmailFormat(pendingSaveContent)
    const clone = isEmail
      ? await createTemplate({
          accountId,
          request: {
            type: templateValue.type,
            typeId: templateValue.typeId,
            scope: request.scope ?? 'account',
            name: request.name,
            description: request.description,
            template: pendingSaveContent,
          },
        }).unwrap()
      : await cloneTemplate({ accountId, templateId: templateValue.id, request }).unwrap()
    try {
      if (!isEmail) {
        await updateTemplate({
          accountId,
          templateId: clone.id,
          request: { template: pendingSaveContent },
        }).unwrap()
      }
      if (origin) {
        if (origin.existingNodeId) {
          await updateNode({
            accountId,
            type: origin.type,
            typeId: origin.typeId,
            nodeId: origin.existingNodeId,
            node: { templateId: clone.id },
          }).unwrap()
        } else {
          await createNode({
            accountId,
            type: origin.type,
            typeId: origin.typeId,
            node: { hierarchyId: calculateHierarchyId(origin.type, origin.context), templateId: clone.id },
          }).unwrap()
        }
      }
    } catch (error) {
      await deleteTemplate({ accountId, templateId: clone.id })
        .unwrap()
        .catch(() => undefined)
      throw error
    }
    setPendingSaveContent(null)
    bypassBlockerRef.current = true
    navigate(`/workspace/setup/templates/template/${clone.id}`, { replace: true, state: origin ? { origin } : undefined })
  }

  const handleTemplateChange = tracked(
    _handleTemplateChange,
    {
      eventName: TrackEvent.TEMPLATE_SAVED,
    },
    { condition: !!templateValue, eventDetailsExtractor: () => getTemplateLibraryTrackingDetails(templateValue!) },
  )

  const handleAITemplateUpdate = (newTemplate: string) => {
    setLocalTemplate(newTemplate)
    setHasAIPendingChanges(true)
  }

  const [prevTemplateId, setPrevTemplateId] = useState(templateId)
  if (prevTemplateId !== templateId) {
    setPrevTemplateId(templateId)
    setLocalTemplate(null)
    setAiIsLoading(false)
    setHasAIPendingChanges(false)
  }

  useEffect(() => {
    bypassBlockerRef.current = false
    currentEditorContentRef.current = null
  }, [templateId])

  const openedTemplateId = templateValue?.id
  const openedTemplateType = templateValue?.type
  const openedTemplateTypeId = templateValue?.typeId
  useEffect(() => {
    if (openedTemplateId && openedTemplateType && openedTemplateTypeId && !user?.isGhosted) {
      trackingClient.track({
        eventName: TrackEvent.TEMPLATE_OPENED,
        eventDetails: getTemplateLibraryTrackingDetails({ id: openedTemplateId, type: openedTemplateType, typeId: openedTemplateTypeId }),
      })
    }
  }, [openedTemplateId, openedTemplateType, openedTemplateTypeId, trackingClient, user?.isGhosted])

  const isLexicalEmail = templateValue ? hasLexicalEmailFormat(templateValue.template) : false
  const isHtmlTemplate = !isLexicalEmail && typeof templateValue?.template.template === 'string'
  const viewOnly = !!templateValue && isAdminManagedTemplate(templateValue) && !hasTemplateAdminScope

  return (
    <div className='truly-full-size-content'>
      {portalElement
        ? createPortal(
            <TemplateEditorHeader templateName={templateValue?.name} label={viewOnly || (!!templateValue && !canEditTemplate) ? 'View template' : undefined} />,
            portalElement,
          )
        : null}
      {templateValue && templateReferencesData ? (
        <Row className='h-100'>
          <Col className='ps-3 pe-0' xs={8}>
            {viewOnly ? (
              <SchedulingEmailViewBox template={templateValue.template} />
            ) : isLexicalEmail ? (
              <EmailTemplateEditorBox
                key={templateValue.id}
                templateValue={templateValue}
                readOnly={!canEditTemplate}
                showDisabledBanner={!canEditTemplate}
                onSave={handleTemplateChange}
                onRequestDeferredSave={setPendingSaveContent}
                loading={isUpdating || isCloning || isCreating}
                bypassBlockerRef={bypassBlockerRef}
              />
            ) : isHtmlTemplate ? (
              <TemplateEditorBox
                templateValue={localTemplate !== null ? { ...templateValue, template: { ...templateValue.template, template: localTemplate } } : templateValue}
                onSave={handleTemplateChange}
                onRequestDeferredSave={setPendingSaveContent}
                onChange={handleEditorChange}
                loading={isUpdating || isCloning || isCreating}
                shouldReset={false}
                readOnly={!canEditTemplate || aiIsLoading}
                showDisabledBanner={!canEditTemplate}
                bypassBlockerRef={bypassBlockerRef}
                hasAIPendingChanges={hasAIPendingChanges}
              />
            ) : (
              <UnopenableTemplateBox />
            )}
          </Col>
          <Col xs={4} className='zocks-bg-color-gray-100 border-start zocks-p-2.0 d-flex flex-column gap-3'>
            <TemplateDetailsBox templateValue={templateValue} readOnly={!canEditTemplate || viewOnly} showControls={!viewOnly} origin={origin} />
            {isHtmlTemplate && !viewOnly ? (
              <TemplateAIChat
                templateId={templateId}
                currentTemplateRef={currentEditorContentRef}
                onTemplateUpdate={handleAITemplateUpdate}
                onLoadingChange={setAiIsLoading}
                readOnly={!canEditTemplate}
                initialAiAgentId={initialAiAgentId}
                initialMessages={initialMessages}
              />
            ) : null}
          </Col>
        </Row>
      ) : (
        <div className='d-flex justify-content-center align-items-center h-100 pt-5'>
          <Spinner />
        </div>
      )}
      {pendingSaveContent !== null && templateValue ? (
        <TemplateDuplicateModal
          show={pendingSaveContent !== null}
          loading={isCloning || isCreating || isUpdating}
          templateValue={templateValue}
          saveAsNew={isLexicalEmail}
          onDuplicate={handleDuplicateFromSystem}
          onCancel={() => setPendingSaveContent(null)}
        />
      ) : null}
    </div>
  )
}
